/**
 * Copy for the in-app Help & User Guide, restructured as data so `HelpPage.jsx` can
 * render it with this app's own components (Card, Badge, SearchInput).
 *
 * `roles` on a section lists which roles it is relevant to — used to dim/explain
 * sections when someone picks a role lens that isn't their own. Keep this file in step
 * with the screens in `routes/navModel.js` and `routes/AppRoutes.jsx`: if a screen or a
 * rule changes, change its section here.
 */

export const ROLES = {
  rep: { label: 'Sales Rep', tone: 'teal', mission: 'Own leads, accounts, deals and activities.', landing: 'Signing in lands you on the Rep dashboard — your open leads, today’s follow-ups, and your personal pipeline.' },
  manager: { label: 'Sales Manager', tone: 'sky', mission: 'Your team’s pipeline, partner leads, approvals and coaching.', landing: 'Signing in lands you on the Manager dashboard — your team’s pipeline, attainment, and who needs coaching. You only ever see your own team and the partners assigned to you.' },
  exec: { label: 'Executive', tone: 'violet', mission: 'Read-only visibility across the whole organisation.', landing: 'Signing in lands you on the Executive dashboard — company-wide revenue, pipeline and forecast, nothing to edit.' },
  admin: { label: 'Super Admin', tone: 'rose', mission: 'Full configuration and data access: users, teams, partners, master data, letterhead, audit and security.', landing: 'Signing in lands you on the Admin dashboard — adoption, data quality, recent audit activity and shortcuts to every configuration screen.' },
  partner: { label: 'Partner', tone: 'amber', mission: 'Submit leads on behalf of yourself or your partner company, and follow them through to deals and projects. Nothing else in the CRM is visible to you.', landing: 'Signing in lands you in the Partner portal — its own menu and dashboard, separate from the CRM.' },
};

const ALL = ['rep', 'manager', 'exec', 'admin'];

export const GROUPS = [
  {
    group: 'Start here',
    sections: [
      {
        id: 'signing-in',
        title: 'Signing in and your password',
        roles: [...ALL, 'partner'],
        blurb: 'Everyone — staff and partners — signs in on the same page with an email and password. Your role decides what you see next: staff land on a CRM dashboard, partners land in the Partner portal. Microsoft, Google and Enterprise SSO buttons are shown but are not available in this build.',
        howtos: [
          {
            q: 'Sign in',
            steps: [
              'Open the sign-in page and enter your work email and password.',
              'You land on your role’s dashboard automatically.',
              'Tick Remember me on a device you trust to stay signed in longer.',
            ],
          },
          {
            q: 'Activate a new account (invitation link)',
            steps: [
              'Open the invitation link from your email — partners get one when they are registered or added to a company team.',
              'Choose a password of at least 12 characters with upper- and lower-case letters, a number and a symbol.',
              'You are taken to the sign-in page; sign in with the new password.',
            ],
          },
          {
            q: 'Forgot your password, or an admin sent you a reset link',
            steps: [
              'Click Forgot? next to the password field and enter your email — or open the reset link an administrator sent you.',
              'The link works once and expires after a short window; request another if it has gone stale.',
              'Set the new password. Every session on the account is signed out, so sign in again with the new password.',
            ],
          },
          {
            q: 'Change your password while signed in',
            steps: [
              'Open your profile menu (top right) and choose Change password.',
              'Enter your current password and the new one. Staff stay signed in on this tab and other sessions are signed out; partners are signed out straight away and sign in again with the new password.',
            ],
          },
        ],
        notes: [{ type: 'tip', text: 'Opening a reset or activation link in a browser where someone else is still signed in is fine — finishing the reset signs that other session out so you can sign in as yourself.' }],
      },
      {
        id: 'sorting',
        title: 'Sorting and searching tables',
        roles: [...ALL, 'partner'],
        blurb: 'Every table can be sorted: click a column header to sort ascending, click again for descending, a third time to go back to the default order. An arrow shows the current direction. Empty values always sink to the bottom.',
        notes: [
          { type: 'tip', text: 'Some columns sort across the whole list on the server (for example every column on the CRM Leads table). Others sort the rows already loaded — on a list with a Load more button, load the rest first if you need a complete ordering.' },
          { type: 'tip', text: 'Most list screens also have a search box and status filters above the table.' },
        ],
      },
    ],
  },
  {
    group: 'Workspace',
    intro: 'Where you land every morning — what needs you today, not the full archive.',
    sections: [
      {
        id: 'dashboard',
        title: 'Dashboards',
        roles: ALL,
        scopes: { rep: 'own', manager: 'team', exec: 'org', admin: 'full' },
        blurb: 'A different dashboard for every role. Reps see their own open leads, overdue follow-ups, pipeline and quota progress. Managers see their own team’s pipeline, a table of their sales representatives’ performance, and the approval queue. Executives see company-wide KPIs. Admins see adoption, data quality and recent audit activity.',
        howtos: [
          {
            q: 'Read your dashboard at a glance',
            steps: [
              'Reps: check Due today and Overdue first — these are follow-ups that will slip if ignored.',
              'Managers: the Sales representative performance table is sorted by quota attainment. A rep with no quota set shows “No quota set” rather than a percentage.',
              'Admins: Licence & adoption shows daily active users (last 24 hours), a 14-day activity trend, and how many active users have not logged in for 15 days.',
            ],
          },
        ],
        notes: [{ type: 'tip', text: 'Admin’s Data quality card lists the records that need clean-up (duplicate accounts, leads with no owner, contacts missing an email) with a shortcut to fix each.' }],
      },
      {
        id: 'feed',
        title: 'Activity Feed',
        roles: ALL,
        blurb: 'A recent-first stream of everything logged against your records — calls, emails, meetings, notes. It is the same data as the Activities screen, framed as “what just happened.”',
        howtos: [
          {
            q: 'Catch up on what changed',
            steps: ['Open Activity Feed from the sidebar.', 'Newest activity is at the top — scroll for history.', 'Click an entry to jump to the lead, account or opportunity it belongs to.'],
          },
        ],
      },
      {
        id: 'team-performance',
        title: 'Team Performance',
        badgeNew: true,
        roles: ['manager', 'admin'],
        scopes: { rep: null, manager: 'team', exec: null, admin: 'all reps' },
        blurb: 'The sales manager’s synopsis of the team plus a page per representative. The period filter defaults to the current month; switch to quarter, year or a custom range. A manager only ever sees their own direct reports; an admin sees every representative.',
        howtos: [
          {
            q: 'Review the team',
            steps: [
              'Open Team Performance. The headline tiles show target vs achieved, pipeline, win rate and lead conversion, each compared with the previous period.',
              'The Rep leaderboard ranks reps by attainment with an on-track / at-risk / behind status. Coaching signals list who needs attention (empty pipeline, no recent activity, overdue follow-ups, stalled deals), alongside the Funnel, Stuck deals, Closing this month and quotations Waiting for your approval.',
              'Click a rep for their detail page: revenue won against target, the last six months, and activity completed per week.',
            ],
          },
          {
            q: 'Set a rep’s monthly target',
            steps: [
              'On the synopsis choose Set monthly target.',
              'Pick the month and enter the revenue target. Attainment recalculates immediately.',
            ],
          },
          {
            q: 'Export the numbers',
            steps: ['Use Export on the synopsis and pick CSV or PDF — the PDF is printed on your organisation’s letterhead.'],
          },
        ],
      },
      {
        id: 'teams',
        title: 'My Team',
        roles: ['manager', 'admin'],
        scopes: { rep: null, manager: 'view own team', exec: null, admin: 'create, edit, assign' },
        blurb: 'Teams are set up by an admin. The admin creates the team, chooses its manager and adds the members; once a team exists, only its manager (and admins) can see it. A manager’s My Team screen is read-only.',
        howtos: [
          {
            q: 'Create a team and assign its manager (admin)',
            steps: [
              'Open My Team and click Create team.',
              'Enter a short code (for example NORDICS) and a name, and choose the Team manager — it is required.',
              'Optionally tick the people to add straight away, then create.',
              'Members are linked to the team’s manager automatically, which is what puts them on that manager’s dashboard and Team Performance.',
            ],
          },
          {
            q: 'Change the manager or the roster (admin)',
            steps: [
              'Open the team. Admins get a Manager dropdown at the top — pick someone else and their members move across.',
              'Use Add members to bring people in, or the ✕ on a member card to remove them. The manager cannot be removed until you assign a new one. Removing keeps history — the person is marked as having left.',
            ],
          },
          {
            q: 'See who is doing what (manager)',
            steps: [
              'Open your team from My Team.',
              'The Team details page shows pipeline, open leads, activities done and overdue follow-ups for the team, with a This month / quarter / year switch.',
              'Each member has a card: their figures, what is up next (overdue in red), what they finished last, and their biggest open deals. Full performance opens that rep’s Team Performance page.',
            ],
          },
        ],
        notes: [{ type: 'tip', text: 'Reps and executives do not see My Team at all.' }],
      },
    ],
  },
  {
    group: 'Sell',
    intro: 'The day-to-day: leads coming in, deals moving forward, projects delivered, quotes going out.',
    sections: [
      {
        id: 'leads',
        title: 'Leads',
        roles: ['rep', 'manager', 'admin'],
        scopes: { rep: 'own', manager: 'team', exec: null, admin: 'full' },
        blurb: 'Every unqualified prospect starts here. Score, status and source tell you where to spend your next ten minutes; converting a lead is what turns it into an account, a contact and — if it is ready — an opportunity. Leads that were referred by a partner carry the partner’s name in the Partner column.',
        howtos: [
          {
            q: 'Work your lead list',
            steps: [
              'Open Leads — the default view is All Open Leads.',
              'Click any column header to sort — Name, Company, Title, Phone, Lead Source, Partner, Score, Lead Status, Est. Value, Owner or Next Follow-up.',
              'Click a lead to open its record, log a call or email, and update its status.',
            ],
          },
          {
            q: 'Change owner or transfer to the queue',
            steps: [
              'Select one or more leads, then choose Change owner from the bulk actions or a lead’s own menu (managers and admins).',
              'To hand an unworked lead back to the pool, choose Transfer to queue. Only leads still in New status can go back; anything already worked is refused with a reason.',
            ],
          },
          {
            q: 'Import leads from a CSV file',
            steps: [
              'Click Import above the lead list (managers and admins only).',
              'Download the sample CSV from the dialog if you are unsure of the format, then upload your file.',
              'Review the preview — valid rows, likely duplicates — and choose what to do with a match: skip, update, fill blanks only, or create anyway.',
              'Start the import; a progress bar shows rows processed and an error report covers anything that failed.',
            ],
          },
          {
            q: 'Say whether a lead wants a product or a project',
            steps: ['On the lead form use the Interest toggle: Product opens a catalogue picker; Project is a free-text name. Leave it unset if you do not know yet.'],
          },
        ],
        notes: [{ type: 'tip', text: 'Leads approved from the Partner portal arrive here with the source “Partner”, assigned to the sales representative the manager chose. Follow them up like any other lead.' }],
      },
      {
        id: 'accounts',
        title: 'Accounts & Contacts',
        roles: ALL,
        scopes: { rep: 'own', manager: 'team', exec: 'view', admin: 'full' },
        blurb: 'Accounts are the companies you sell to; Contacts are the people inside them. An account’s Overview tab shows company details and lifecycle stage; its Hierarchy tab shows parent/subsidiary relationships.',
        howtos: [
          {
            q: 'Open an account and see the full picture',
            steps: ['Search or filter the Accounts list and click through to a record.', 'Related contacts, opportunities and activities each get their own tab on the same record.'],
          },
          {
            q: 'Never miss a contact’s anniversary',
            steps: ['Open Contacts and click Anniversaries to see work anniversaries in the next 7 days.', 'Send a quick note with the Customer Work Anniversary email template.'],
          },
        ],
      },
      {
        id: 'opportunities',
        title: 'Opportunities & Pipeline',
        roles: ALL,
        scopes: { rep: 'own', manager: 'team', exec: 'view', admin: 'full' },
        blurb: 'A deal in progress, tracked stage by stage. The same records render two ways: a sortable list, or a kanban board.',
        howtos: [
          {
            q: 'Create or edit an opportunity',
            steps: [
              'Click New opportunity. The Account and Competitor fields are searchable dropdowns — type to find one.',
              'Sales owner: a sales rep’s own opportunities are always owned by them and the field is locked. Admins and managers can assign a different sales owner.',
              'Missing a product/service or a competitor? Use the Add product / service and Add competitor links next to the field — a small form opens, and the dropdown refreshes with the new entry. Reps may add these two things only; everything else in Master Data stays admin-only.',
            ],
          },
          {
            q: 'Move a deal forward',
            steps: [
              'Toggle to the board view and drag a card to its new stage, or open the record and change the stage there — the full stage history is kept either way.',
              'Closing as Lost requires a lost reason so the team learns from every deal that does not land.',
            ],
          },
        ],
      },
      {
        id: 'projects',
        title: 'Projects',
        badgeNew: true,
        roles: ['rep', 'manager', 'exec', 'admin'],
        scopes: { rep: 'own', manager: 'team', exec: 'view', admin: 'full' },
        blurb: 'The delivery side of a won deal: a project with a customer, a related deal, a project manager, start and expected completion dates, a status and a list of milestones. Partners see the projects that came from their own leads, read-only.',
        howtos: [
          {
            q: 'Create a project',
            steps: [
              'Open Sell → Projects and click New project.',
              'Give it a name and pick the Related deal — the customer is filled in from the deal. Choose the project manager, status and dates.',
              'Save, then open the project to add milestones.',
            ],
          },
          {
            q: 'Track milestones',
            steps: [
              'Open a project. Use Add milestone with a name and due date.',
              'Change a milestone’s status (Pending, In progress, Completed) from its dropdown; the progress bar follows. Overdue milestones show in red.',
              'When a project’s status or a milestone changes, the partner who referred the deal is notified.',
            ],
          },
        ],
        notes: [{ type: 'tip', text: 'You see projects you manage or created, and projects on deals owned by you or (for managers) your team.' }],
      },
      {
        id: 'quotations',
        title: 'Quotations & Approvals',
        roles: ['rep', 'manager', 'admin'],
        scopes: { rep: 'own', manager: 'team, approve', exec: null, admin: 'full' },
        blurb: 'A formal price quote with line items, discounts, terms and a revision history. Above a manager’s discretionary discount threshold, a quote needs sign-off before it can be sent.',
        howtos: [
          {
            q: 'Build and send a quote',
            steps: [
              'Create the quotation against an opportunity and add line items from the product catalogue.',
              'Every quotation needs Terms & Conditions, and Payment terms can run to several lines — write them in the form.',
              'Submit for approval if the discount needs sign-off; otherwise send it directly.',
              'Send emails the quote to the customer. Tick Attach PDF to include it as a PDF on your organisation’s letterhead, or use the PDF button to download it first.',
            ],
          },
          {
            q: 'Approve or reject a pending quote (managers)',
            steps: ['Open the Quote approvals tab under Quotations.', 'Review the discount and margin against the deal.', 'Approve, ask for a revision, or reject with a reason the rep can act on.'],
          },
        ],
        notes: [{ type: 'tip', text: 'A revised quotation is a new row sharing the same root — the original terms stay on file after a revision supersedes them.' }],
      },
    ],
  },
  {
    group: 'Engage',
    intro: 'Everything that keeps a prospect warm between meetings.',
    sections: [
      {
        id: 'activities',
        title: 'Activities & Follow-ups',
        roles: ['rep', 'manager', 'admin'],
        scopes: { rep: 'own', manager: 'team', exec: null, admin: 'full' },
        blurb: 'Calls, meetings, visits, emails and tasks — logged against a lead, contact, account or opportunity. The same screen switches between a list, a calendar month view and a follow-ups queue. Today and Upcoming include anything scheduled or due in that window.',
        howtos: [
          {
            q: 'Log an activity',
            steps: ['From a record’s page, click Log activity and choose the type.', 'Fill in the details and, if there is a next step, set a follow-up date.'],
          },
          {
            q: 'Mark an activity complete',
            steps: [
              'Calls: record the start, end and duration, the call outcome (Interested, Not interested, Call back, Meeting required, Quotation required) and notes.',
              'Visits: record the date, start/end time, location, GPS location, purpose (Requirement discussion, Product demo, Negotiation, Technical discussion, Payment follow-up, Relationship visit), visit notes, customer feedback, next action and next follow-up date.',
              'Anything else (tasks, emails, notes): add a note and complete it.',
            ],
          },
          {
            q: 'Switch views',
            steps: ['Use the toggle at the top of Activities to move between List, Calendar and Follow-ups.'],
          },
        ],
      },
      {
        id: 'templates',
        title: 'Email Templates',
        roles: ['rep', 'manager', 'admin'],
        scopes: { rep: 'own', manager: 'team', exec: null, admin: 'full' },
        blurb: 'Reusable email copy with live merge fields — {{lead.first_name}}, {{contact.company}} and more — so nobody types “Dear [NAME]” by hand. Shared starting points cover cold outreach, meeting follow-up, proposal sent, work-anniversary goodwill and re-engaging a cold lead.',
        howtos: [
          {
            q: 'Send a templated email',
            steps: ['From a lead or contact’s record click Email and pick a template.', 'The preview shows the merge fields resolved for that record.', 'Send — it goes out through the organisation’s email provider and logs itself as an activity.'],
          },
          {
            q: 'Create your own template',
            steps: ['Open Email Templates and click New template.', 'Write the subject and body, dropping in merge tokens from the picker.', 'Mark it Shared for the team, or keep it personal.'],
          },
        ],
      },
      {
        id: 'email',
        title: 'Email',
        roles: ['rep', 'manager', 'admin'],
        scopes: { rep: 'own', manager: 'team', exec: null, admin: 'full' },
        blurb: 'A standalone inbox for everything you have sent through the CRM — compose to any lead, contact or account, then track whether it was read. Messages are sent by a background email worker, so a message shows as queued for a few seconds before it is sent.',
        howtos: [
          {
            q: 'Compose an email',
            steps: ['Open Email and click Compose.', 'Search for the recipient — a lead, contact or account — rather than typing a bare address, so the send is tied to a real record.', 'Write it or start from a template, optionally attach a PDF, and send.'],
          },
          {
            q: 'See who has read your emails',
            steps: ['The list shows every email you sent, newest first, with an Opened marker and a count once the recipient’s mail client loads it.', 'Filter by Status (queued, sent, failed) or Read (opened / not opened).'],
          },
        ],
        notes: [{ type: 'tip', text: 'Open tracking relies on a tiny image; mail clients that block images can make “not opened” mean “opened, but we could not tell.”' }],
      },
      {
        id: 'campaigns',
        title: 'Campaigns',
        roles: ALL,
        scopes: { rep: 'own', manager: 'team', exec: 'view', admin: 'full' },
        blurb: 'A tracked marketing push — a webinar, a trade show, an ad push — with its own budget, spend and the leads and opportunities it produced. ROI and cost per lead are computed from what those leads actually did.',
        howtos: [
          {
            q: 'Check a campaign’s return',
            steps: ['Open Campaigns and pick one from the list.', 'Leads generated, conversion and ROI come from real linked records. An ROI of “—” means no spend has been recorded yet; it is not a 0% return.'],
          },
        ],
      },
    ],
  },
  {
    group: 'Analyse',
    intro: 'The rear-view mirror and the road ahead.',
    sections: [
      {
        id: 'reports',
        title: 'Reports & Forecast',
        roles: ALL,
        scopes: { rep: 'view', manager: 'view', exec: 'view', admin: 'full' },
        blurb: 'Focused reports — Overview, Leads, Opportunities, Activities, Revenue, Forecast and Territories — each answering one question well.',
        howtos: [
          {
            q: 'Find the report you need',
            steps: ['Open Reports and use the sub-menu to jump between them.', 'Every report reflects your data scope — a rep sees their own numbers, a manager the team’s, an executive the whole organisation.'],
          },
        ],
      },
      {
        id: 'targets',
        title: 'Targets & Quotas',
        roles: ALL,
        blurb: 'Quota and attainment per representative, so the conversation starts with whoever is furthest behind. Managers can also set a rep’s monthly revenue target from Team Performance.',
      },
    ],
  },
  {
    group: 'Partner portal',
    intro: 'For partner logins — individual partners and partner companies. Partners have their own menu and never see the CRM.',
    sections: [
      {
        id: 'partner-overview',
        title: 'The Partner portal and its roles',
        badgeNew: true,
        roles: ['partner'],
        scopes: { partner: 'own / company' },
        blurb: 'There are three kinds of partner login. An Individual partner submits and follows their own leads. A Company Partner Admin sees every lead the company submits, manages the company’s team and gets a Team Dashboard. A Company Member submits and sees only their own leads. The menu is Dashboard, Deals / Leads, Projects, Notifications and Profile — plus Team Dashboard and Team Users for company admins.',
        howtos: [
          {
            q: 'Read your dashboard',
            steps: [
              'The Leads row counts your submissions: total, in review, approved, rejected. A banner appears when leads need more information.',
              'The Deals row counts active and won deals from your approved leads; company admins also see team figures.',
              'Below are a status chart, a 12-month submissions chart and your latest activity, comments and notifications.',
            ],
          },
        ],
      },
      {
        id: 'partner-leads',
        title: 'Submitting and following leads',
        roles: ['partner'],
        scopes: { partner: 'own / company' },
        blurb: 'A lead moves Draft → In review → (Information requested ↔ In review) → Approved or Rejected. Your Sales Manager reviews it; once approved it becomes a CRM lead owned by a sales representative who follows it up.',
        howtos: [
          {
            q: 'Add a lead',
            steps: [
              'Click Add new lead and fill in the company, contact person, email, mobile, industry, country, product / service interest and a description. Estimated value and purchase timeline are optional.',
              'A warning appears if the customer looks like a lead that already exists — it does not block you, the Sales Manager decides.',
              'Attach any files, then choose Save as draft (keep working on it) or Submit for review.',
            ],
          },
          {
            q: 'Follow a lead',
            steps: [
              'Open a lead from Deals / Leads. The header shows its status, any rejection reason or “more information needed” request, and the assigned sales representative.',
              'The Conversation tab is where you and the sales team exchange comments and see logged calls and emails. Documents holds attachments.',
              'If more information was requested, edit the lead and choose Save & resubmit — it returns to review.',
            ],
          },
          {
            q: 'Assign a lead to a team member (company admin)',
            steps: ['On a lead, open the Assignment tab, pick a member and save. This is bookkeeping for your own company — it does not give the member access to the lead.'],
          },
        ],
        notes: [{ type: 'tip', text: 'You can edit a lead only while it is a draft or information has been requested. Once it is in review, add a comment instead.' }],
      },
      {
        id: 'partner-team',
        title: 'Managing your company team',
        roles: ['partner'],
        scopes: { partner: 'company admin' },
        blurb: 'Company Partner Admins add their colleagues, activate or deactivate them and see how each member is contributing.',
        howtos: [
          {
            q: 'Add a team member',
            steps: ['Open Team Users and click Add member; enter their name, email and mobile.', 'They receive an activation email to set a password. Re-send the invitation from the same screen if it is lost.'],
          },
          {
            q: 'See team contribution',
            steps: ['Team Dashboard shows top contributors and a table of each member’s submitted, in-review, approved and rejected leads and active deals.'],
          },
        ],
      },
      {
        id: 'partner-deals-projects',
        title: 'Deals, projects and notifications',
        roles: ['partner'],
        scopes: { partner: 'read-only' },
        blurb: 'Deals shows the stage and value of deals that came from your approved leads. Projects shows delivery projects on those deals with their milestones. Both are read-only.',
        howtos: [
          {
            q: 'Check a project',
            steps: ['Open Projects, sort or filter by status, and click a row.', 'The detail shows the project ID, customer, related deal, manager, dates, progress and every milestone.'],
          },
          {
            q: 'Stay informed',
            steps: ['The bell in the top bar and the Notifications page list lead status changes, comments, assignments and project or milestone updates. Mark one or all as read.'],
          },
        ],
        notes: [{ type: 'tip', text: 'Internal notes the sales team keeps about your leads are never shown to partners.' }],
      },
    ],
  },
  {
    group: 'Administration',
    intro: 'The controls that shape what everyone else sees. Most are admin-only; the partner screens are shared with Sales Managers.',
    sections: [
      {
        id: 'admin-users',
        title: 'Users & Roles',
        roles: ['admin'],
        blurb: 'Who exists in the system, what roles they hold, and exactly which screen-and-action pairs those roles unlock. A person can hold several roles at once; the permissions are the union, and an explicit deny always wins.',
        howtos: [
          {
            q: 'Add a new person',
            steps: ['Open Users & Roles and click New user.', 'Enter their name, email and starting role.', 'They receive an invitation to set their own password.'],
          },
          {
            q: 'Adjust what a role can do',
            steps: ['Open Role permissions under Users & Roles.', 'Toggle a screen-and-action pair on or off for the role.'],
          },
        ],
        notes: [{ type: 'tip', text: 'Partner logins are created from Partner management, not here.' }],
      },
      {
        id: 'admin-partners',
        title: 'Partner management',
        badgeNew: true,
        roles: ['manager', 'admin'],
        scopes: { rep: null, manager: 'view own partners', exec: null, admin: 'full' },
        blurb: 'Register and manage the individuals and companies that refer leads. Each partner is tied to one Sales Manager when it is registered — that manager is notified of its leads and reviews them. Partners with no manager are visible to admins only. A Sales Manager can view their own partners but cannot edit them, change their status or send reset links.',
        howtos: [
          {
            q: 'Register a partner',
            steps: [
              'Open Administration → Partners and click Register partner. Choose Individual or Company.',
              'Choose the Sales manager (required), then fill in the contact details. Company partners also need the admin’s login email.',
              'Register & send invitation — the partner’s login is created and an activation link is emailed.',
            ],
          },
          {
            q: 'Edit a partner or change its Sales Manager (admin)',
            steps: ['Click Edit on a row, or open the partner and choose Edit partner details.', 'Change the details or the Sales manager and save. The login email is managed through the invitation and is not changed here.'],
          },
          {
            q: 'Activate, deactivate or block (admin)',
            steps: ['Use the row buttons. Deactivating or blocking a company suspends every member’s access; leads and history stay available to the CRM team. Reactivating restores access.'],
          },
          {
            q: 'Send a partner a password reset link (admin)',
            steps: ['Open the partner; each user has a Send reset link button (or Resend invitation if they never activated).', 'They receive a single-use link to set a new password. If email cannot be delivered, the link is shown so you can pass it on.'],
          },
        ],
      },
      {
        id: 'partner-review',
        title: 'Partner Lead Review',
        badgeNew: true,
        roles: ['manager', 'admin'],
        scopes: { rep: null, manager: 'own partners', exec: null, admin: 'all partners' },
        blurb: 'The Sales Manager’s queue of leads submitted by partners. You see only leads from partners assigned to you; admins see all.',
        howtos: [
          {
            q: 'Review a lead',
            steps: [
              'Open Partner Lead Review and pick a tab — In review, Information requested, Approved, Rejected or All. A duplicate warning flags a lead that may already exist.',
              'Open a lead to read the details, documents and conversation. Internal-only notes are visible to you and never to the partner.',
            ],
          },
          {
            q: 'Approve and assign a sales representative',
            steps: ['Choose Approve, pick a sales representative from your team (optional now) and add remarks.', 'Approving creates the CRM lead once and notifies the partner. Use Assign sales rep later to hand an approved lead to someone else.'],
          },
          {
            q: 'Reject, or ask for more information',
            steps: [
              'Reject needs a proper reason of at least 20 characters — the partner reads it. A character counter shows how much is left.',
              'Request information sends a question back; the lead returns to review when the partner replies.',
            ],
          },
        ],
      },
      {
        id: 'admin-master',
        title: 'Master Data',
        roles: ['admin'],
        blurb: 'The dropdown lists every other screen reads from — lead sources, industries, opportunity stages, lost reasons, territories, products and more. Everyone can read these; only an admin edits the lists, apart from reps adding a product/service or competitor from the opportunity form.',
        howtos: [
          {
            q: 'Add or change a value',
            steps: ['Open Master Data and pick the list, for example Products / Services or Competitors.', 'Add, rename or reorder values. Retiring a value marks it inactive rather than deleting it, so existing records keep reading correctly.'],
          },
        ],
      },
      {
        id: 'admin-letterhead',
        title: 'Letterhead',
        badgeNew: true,
        roles: ['admin'],
        blurb: 'Upload your organisation’s official letterhead as HTML once. Every generated PDF — quotations, the Team Performance export and other documents sent to clients — is printed on it.',
        howtos: [
          {
            q: 'Set up the letterhead',
            steps: ['Open Administration → Letterhead and upload the HTML file (one per organisation).', 'Use the preview to confirm how a document looks on it, then save.'],
          },
        ],
      },
      {
        id: 'admin-other',
        title: 'Integrations, Automation, Audit & Security',
        roles: ['admin'],
        blurb: 'Email providers (SendGrid or SMTP) are connected by an administrator, with the key encrypted when saved; the default provider carries every email the CRM sends, including partner invitations and reset links. Workflow Builder, the Audit Log (who changed what, when) and Security (password policy, sessions) are also here.',
        notes: [{ type: 'tip', text: 'Check the email worker is running if messages stay Queued — quotation and template emails are sent by it. Invitation and reset-link emails are sent directly.' }],
      },
    ],
  },
];

/** The "who can do what" cheat sheet. */
export const MATRIX = [
  { area: 'Leads', rep: 'Own', manager: 'Team', exec: null, admin: 'Full', partner: 'Own (submitted)' },
  { area: 'Accounts & Contacts', rep: 'Own', manager: 'Team', exec: 'View', admin: 'Full', partner: null },
  { area: 'Opportunities & Pipeline', rep: 'Own (owner locked)', manager: 'Team, assign owner', exec: 'View', admin: 'Full', partner: 'Deals: read-only' },
  { area: 'Projects', rep: 'Own', manager: 'Team', exec: 'View', admin: 'Full', partner: 'Read-only' },
  { area: 'Quotations & Approvals', rep: 'Own', manager: 'Team + approve', exec: null, admin: 'Full', partner: null },
  { area: 'Activities & Follow-ups', rep: 'Own', manager: 'Team', exec: null, admin: 'Full', partner: null },
  { area: 'Email Templates & Email', rep: 'Own', manager: 'Team', exec: null, admin: 'Full', partner: null },
  { area: 'Campaigns', rep: 'Own', manager: 'Team', exec: 'View', admin: 'Full', partner: null },
  { area: 'Reports, Forecast, Targets', rep: 'View', manager: 'View', exec: 'View', admin: 'Full', partner: null },
  { area: 'Team Performance', rep: null, manager: 'Own team', exec: null, admin: 'All reps', partner: null },
  { area: 'My Team', rep: null, manager: 'View own team', exec: null, admin: 'Create, edit, assign', partner: null },
  { area: 'Partner management', rep: null, manager: 'View own partners', exec: null, admin: 'Full', partner: null },
  { area: 'Partner Lead Review', rep: null, manager: 'Own partners', exec: null, admin: 'All partners', partner: null },
  { area: 'Partner portal (leads, team, projects)', rep: null, manager: null, exec: null, admin: null, partner: 'Own / company' },
  { area: 'Letterhead, Master Data, Users & Roles', rep: null, manager: null, exec: null, admin: 'Full', partner: null },
];
